import { Plus, Trash2 } from 'lucide-react';

import type { Excecao, Recorrencia } from '@/api/calendarioForense';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type Modo = 'nao_ha' | 'transferido' | 'acrescido' | 'periodo';

export interface LinhaExcecao {
  ano: string;
  modo: Modo;
  para: string;
  ate: string;
}

export function excecoesParaLinhas(excecoes: Record<string, Excecao> | null): LinhaExcecao[] {
  return Object.entries(excecoes ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([ano, v]) => {
      if (v === null) return { ano, modo: 'nao_ha', para: '', ate: '' };
      if (typeof v === 'string') return { ano, modo: 'transferido', para: v, ate: '' };
      if (v.ate) return { ano, modo: 'periodo', para: v.para, ate: v.ate };
      return {
        ano,
        modo: v.manter_original ? 'acrescido' : 'transferido',
        para: v.para,
        ate: '',
      };
    });
}

/** Linhas → o JSON da regra. Linha sem ano fica de fora; a validação do resto é do backend. */
export function linhasParaExcecoes(linhas: LinhaExcecao[]): Record<string, Excecao> | null {
  const saida: Record<string, Excecao> = {};
  for (const l of linhas) {
    const ano = l.ano.trim();
    if (!ano) continue;
    if (l.modo === 'nao_ha') saida[ano] = null;
    else if (l.modo === 'periodo') saida[ano] = { para: l.para, ate: l.ate };
    else if (l.modo === 'acrescido') saida[ano] = { para: l.para, manter_original: true };
    else saida[ano] = l.para;
  }
  return Object.keys(saida).length ? saida : null;
}

/** Ao trocar a recorrência: o modo que a nova não tem vira o equivalente dela. */
export function adaptarLinhas(linhas: LinhaExcecao[], recorrencia: Recorrencia): LinhaExcecao[] {
  const periodo = recorrencia === 'periodo_anual';
  return linhas.map((l) => {
    if (periodo && (l.modo === 'transferido' || l.modo === 'acrescido')) return { ...l, modo: 'periodo' };
    if (!periodo && l.modo === 'periodo') return { ...l, modo: 'transferido', ate: '' };
    return l;
  });
}

/** O primeiro ano que aparece em duas linhas (a última venceria calada). */
export function anoRepetido(linhas: LinhaExcecao[]): string | null {
  const vistos = new Set<string>();
  for (const l of linhas) {
    const ano = l.ano.trim();
    if (!ano) continue;
    if (vistos.has(ano)) return ano;
    vistos.add(ano);
  }
  return null;
}

interface Props {
  recorrencia: Recorrencia;
  linhas: LinhaExcecao[];
  onChange: (linhas: LinhaExcecao[]) => void;
}

/** Exceção por ano: o feriado que um ato do tribunal remanejou, ou que naquele ano não há. */
export function ExcecoesEditor({ recorrencia, linhas, onChange }: Props) {
  const periodo = recorrencia === 'periodo_anual';
  const mudar = (i: number, parcial: Partial<LinhaExcecao>) =>
    onChange(linhas.map((l, j) => (j === i ? { ...l, ...parcial } : l)));

  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium">Exceções por ano</legend>
      <p className="text-xs text-muted-foreground">
        Para o ano em que um ato transferiu {periodo ? 'o período' : 'o dia'} ou não há.
      </p>
      {linhas.map((l, i) => (
        <div key={i} className="grid grid-cols-[5.5rem_1fr_auto] items-end gap-2 sm:grid-cols-[5.5rem_12rem_1fr_auto]">
          <div className="space-y-1">
            <Label htmlFor={`exc-ano-${i}`}>Ano</Label>
            <Input
              id={`exc-ano-${i}`}
              inputMode="numeric"
              maxLength={4}
              value={l.ano}
              onChange={(e) => mudar(i, { ano: e.target.value.replace(/\D/g, '') })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`exc-modo-${i}`}>Naquele ano</Label>
            <Select value={l.modo} onValueChange={(v) => mudar(i, { modo: v as Modo })}>
              <SelectTrigger id={`exc-modo-${i}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nao_ha">Não há</SelectItem>
                {periodo ? (
                  <SelectItem value="periodo">Outro período</SelectItem>
                ) : (
                  <>
                    <SelectItem value="transferido">Transferido para</SelectItem>
                    <SelectItem value="acrescido">Mantido, e também em</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          {l.modo !== 'nao_ha' && (
            <div className="col-span-2 flex gap-2 sm:col-span-1">
              <div className="flex-1 space-y-1">
                <Label htmlFor={`exc-para-${i}`}>{l.modo === 'periodo' ? 'De' : 'Data'}</Label>
                <Input
                  id={`exc-para-${i}`}
                  type="date"
                  value={l.para}
                  onChange={(e) => mudar(i, { para: e.target.value })}
                />
              </div>
              {l.modo === 'periodo' && (
                <div className="flex-1 space-y-1">
                  <Label htmlFor={`exc-ate-${i}`}>Até</Label>
                  <Input
                    id={`exc-ate-${i}`}
                    type="date"
                    value={l.ate}
                    onChange={(e) => mudar(i, { ate: e.target.value })}
                  />
                </div>
              )}
            </div>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11"
            aria-label={`Remover a exceção de ${l.ano || 'ano sem número'}`}
            onClick={() => onChange(linhas.filter((_, j) => j !== i))}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="min-h-11"
        onClick={() =>
          onChange([
            ...linhas,
            {
              ano: '',
              modo: periodo ? 'periodo' : 'transferido',
              para: '',
              ate: '',
            },
          ])
        }
      >
        <Plus className="mr-1 h-4 w-4" />
        Adicionar exceção
      </Button>
    </fieldset>
  );
}
